import { useState } from "react";
import "./GoToAdvAnalytics.scss";
import images from "../../../../../utilities/images/images";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import analyticsApi from "../../../../../redux/exam_analytics/analyticsApi";
import { normalizeMistakeTopicQuestion } from "../../../../MistakeBook/mistakeBookApi";
import bookIcon from "../assets/mb-cta-book.svg";
import chevronWhite from "../assets/mb-cta-chevron-white.svg";
import analyticsIcon from "../assets/mb-cta-analytics.svg";
import chevronDark from "../assets/mb-cta-chevron-dark.svg";
import { resolveAttemptId } from "../../../utils/resolveAttemptId";

const AITS_YEARWISE_SOURCES = new Set(["paperlisting", "yearwise"]);

const isIncorrectQuestion = (question = {}) => {
  const options = Array.isArray(question.option)
    ? question.option
    : Array.isArray(question.options)
      ? question.options
      : [];

  const analyticsSelected = options.filter(
    (opt) => Number(opt?.is_user_correct_ans) === 1
  );
  if (analyticsSelected.length > 0) {
    return !analyticsSelected.every((opt) => Number(opt?.is_correct) === 1);
  }

  const userAnswer = String(
    question.user_answer ?? question.your_answer ?? ""
  ).toUpperCase();
  return userAnswer === "WRONG" || userAnswer === "INCORRECT";
};

const GoToAdvAnalytics = () => {
  const history = useHistory();
  const { paperId, testId, testType, testDetails } = useSelector(
    (state) => state.examSlice
  );
  const { basicAnalytics } = useSelector((state) => state.analyticsSlice);
  const [loadingMistakeBook, setLoadingMistakeBook] = useState(false);

  const source = testDetails?.source;
  const showMistakeBookCta =
    testDetails?.practiceMode !== "practice" &&
    AITS_YEARWISE_SOURCES.has(String(source || ""));

  const goToAnalytics = () => {
    const attemptCount = resolveAttemptId(
      basicAnalytics?.data?.selected_attempt,
      basicAnalytics?.data?.attempts || []
    );
    history.push("/adv-analytics", { attempt_count: attemptCount });
  };

  const openIncorrectInMistakeBook = async () => {
    if (loadingMistakeBook) return;

    const finalPaperId = paperId || testDetails?.paper_id || testDetails?.paperId;
    const finalSetId = testDetails?.setId || testDetails?.set_id;
    const finalPaperType =
      testType?.id ?? testDetails?.paperType ?? testDetails?.paper_type;
    const finalTestId =
      testId ||
      testDetails?.testId ||
      testDetails?.setId ||
      testDetails?.set_id;
    const attemptCount = resolveAttemptId(
      basicAnalytics?.data?.selected_attempt,
      basicAnalytics?.data?.attempts || []
    );

    if ((!finalPaperId && !finalSetId) || finalPaperType == null) {
      history.push("/practice/mistake-book");
      return;
    }

    setLoadingMistakeBook(true);
    try {
      const response = await analyticsApi.getPracticeSolution({
        paperId: finalPaperId,
        paperType: finalPaperType,
        paper_type: finalPaperType,
        setId: finalSetId,
        set_id: finalSetId,
        plannerTestId:
          testDetails?.planner_test_id || finalTestId || undefined,
        attemptCount,
        attempt_count: attemptCount,
      });

      const rawList = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response)
            ? response
            : [];

      const incorrectQuestions = rawList
        .filter(isIncorrectQuestion)
        .map((item, index) => normalizeMistakeTopicQuestion(item, index));

      if (!incorrectQuestions.length) {
        history.push("/practice/mistake-book");
        return;
      }

      history.push("/practice/mistake-book/question-detail", {
        fromMistakeBook: true,
        fromAnalyticsMistakeBook: true,
        paper_type: finalPaperType,
        paperType: finalPaperType,
        topicName: "Tag your mistakes",
        subjectName: "",
        questions: incorrectQuestions,
        questionIndex: 0,
        qid: incorrectQuestions[0]?.qid,
        question: incorrectQuestions[0],
        activeTab: "mistakes",
        listPath: "/analytics",
        listState: {
          from_submit: true,
        },
      });
    } catch (_) {
      history.push("/practice/mistake-book");
    } finally {
      setLoadingMistakeBook(false);
    }
  };

  if (showMistakeBookCta) {
    return (
      <div className="analytics-cta-row">
        <button
          type="button"
          className="analytics-cta-row__mistake"
          onClick={openIncorrectInMistakeBook}
          disabled={loadingMistakeBook}
        >
          <div className="analytics-cta-row__mistake-left">
            <span className="analytics-cta-row__mistake-icon" aria-hidden="true">
              <img src={bookIcon} alt="" />
            </span>
            <span className="analytics-cta-row__mistake-copy">
              <span className="analytics-cta-row__mistake-title">Mistake Book</span>
              <span className="analytics-cta-row__mistake-subtitle">
                Review and fix errors
              </span>
            </span>
          </div>
          {loadingMistakeBook ? (
            <ClipLoader size={20} color="#fff" />
          ) : (
            <img
              className="analytics-cta-row__chevron"
              src={chevronWhite}
              alt=""
              aria-hidden="true"
            />
          )}
        </button>

        <button
          type="button"
          className="analytics-cta-row__analytics"
          onClick={goToAnalytics}
        >
          <div className="analytics-cta-row__analytics-left">
            <img src={analyticsIcon} alt="" aria-hidden="true" />
            <span>See Your Advanced Analytics</span>
          </div>
          <img
            className="analytics-cta-row__chevron analytics-cta-row__chevron--dark"
            src={chevronDark}
            alt=""
            aria-hidden="true"
          />
        </button>
      </div>
    );
  }

  return (
    <div
      className="goToAdvAnalytics"
      onClick={goToAnalytics}
      style={{ cursor: "pointer" }}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") goToAnalytics();
      }}
    >
      <div className="goToAdvAnalytics__left">
        <img src={images.analytics} alt="analytics" />
        <div>See Your Advanced Analytics</div>
      </div>

      <div className="goToAdvAnalytics__rightbtn">
        <img src={images.backBtn} alt="right" />
      </div>
    </div>
  );
};

export default GoToAdvAnalytics;
